import { create } from 'zustand';
import { devtools } from 'zustand/middleware';
import { documentApi } from '../services/api';

interface PendingJob {
  jobId: string;
  docType: string;
  docId: string;
  pollCount: number;
  timer?: NodeJS.Timeout;
}

interface JobPollingState {
  pendingJobs: Record<string, PendingJob>;

  // Actions
  startPolling: (jobId: string, docType: string, docId: string) => void;
  stopPolling: (jobId: string) => void;
  clearAllPolling: () => void;
}

const MAX_POLLS = 60;

export const useJobPollingStore = create<JobPollingState>()(
  devtools((set, get) => ({
    pendingJobs: {},

    startPolling: (jobId: string, docType: string, docId: string) => {
      const { pendingJobs } = get();

      console.log('[JobPolling] Starting polling for:', { jobId, docType, docId });

      // Don't start if already polling this job
      if (pendingJobs[jobId]) {
        console.log('[JobPolling] Already polling job:', jobId);
        return;
      }

      // Create new job entry
      const job: PendingJob = {
        jobId,
        docType,
        docId,
        pollCount: 0,
      };

      set({
        pendingJobs: {
          ...pendingJobs,
          [jobId]: job,
        },
      });

      console.log('[JobPolling] Job added to pending jobs, starting poll');

      // Start polling
      const poll = async () => {
        const state = get();
        const currentJob = state.pendingJobs[jobId];

        if (!currentJob) {
          // Job was removed, stop polling
          return;
        }

        const newPollCount = currentJob.pollCount + 1;

        console.log(`[JobPolling] Poll #${newPollCount} for job ${jobId}`);

        try {
          const response = await documentApi.getJobStatus(jobId);

          console.log('[JobPolling] Job status response:', { jobId, status: response?.status });

          if (response?.status) {
            const status = response.status.toLowerCase();

            if (status.startsWith('complete')) {
              console.log('[JobPolling] Job completed successfully:', jobId);
              // Success - dispatch event for notifications
              window.dispatchEvent(new CustomEvent('job-complete', {
                detail: { jobId, docType, docId, success: true }
              }));

              // Remove from polling
              get().stopPolling(jobId);
              return;
            } else if (status.startsWith('error')) {
              console.log('[JobPolling] Job failed:', jobId, status);
              // Error - dispatch event for notifications
              const errorDetail = status.includes('404') ? '404' : 'general';
              window.dispatchEvent(new CustomEvent('job-complete', {
                detail: { jobId, docType, docId, success: false, error: errorDetail }
              }));

              // Remove from polling
              get().stopPolling(jobId);
              return;
            }

            // Still processing, continue polling
            if (newPollCount < MAX_POLLS) {
              let nextInterval;
              if (newPollCount <= 30) {
                nextInterval = 1500;
              } else if (newPollCount <= 45) {
                nextInterval = 5000;
              } else {
                nextInterval = 10000;
              }

              const timer = setTimeout(poll, nextInterval);

              // Update job with new timer and poll count
              set(state => ({
                pendingJobs: {
                  ...state.pendingJobs,
                  [jobId]: {
                    ...state.pendingJobs[jobId],
                    pollCount: newPollCount,
                    timer,
                  },
                },
              }));
            } else {
              // Timeout - dispatch event
              window.dispatchEvent(new CustomEvent('job-complete', {
                detail: { jobId, docType, docId, success: false, error: 'timeout' }
              }));

              // Remove from polling
              get().stopPolling(jobId);
            }
          }
        } catch (error) {
          console.error('Error polling job status:', error);

          // Dispatch error event
          window.dispatchEvent(new CustomEvent('job-complete', {
            detail: { jobId, docType, docId, success: false, error: 'polling-error' }
          }));

          // Remove from polling
          get().stopPolling(jobId);
        }
      };

      // Start first poll
      poll();
    },

    stopPolling: (jobId: string) => {
      const { pendingJobs } = get();
      const job = pendingJobs[jobId];

      if (job?.timer) {
        clearTimeout(job.timer);
      }

      const { [jobId]: removed, ...remaining } = pendingJobs;
      set({ pendingJobs: remaining });
    },

    clearAllPolling: () => {
      const { pendingJobs } = get();

      Object.values(pendingJobs).forEach(job => {
        if (job.timer) {
          clearTimeout(job.timer);
        }
      });

      set({ pendingJobs: {} });
    },
  }))
);
